import { Options, PublicBundleData, ShopifyStorefrontOptions } from '../types/bundleData';
import { getOptions } from '../utils/options';
import { SHOPIFY_APP_PROXY_URL } from '../constants/api';
import { BundleData as StorefrontBundleData } from '../types/bundle';
import { request, shopifyAppProxyRequest } from '../utils/request';
import { mapOnlineStoreToPublicBundleData } from '../utils/bundleData';

/**
 * Load bundle data from the specified source
 *
 * @param id - The external product ID (Shopify product ID)
 * @param options - Configuration for data source and parameters
 * @param options.source - The source of the data (online_store or shopify_storefront)
 * @param options.country_code - The country code for the online store
 * @param options.storefront_access_token - The storefront access token for the Shopify Storefront API
 * @returns Promise resolving to bundle data result with success/error handling
 *
 * @example
 * ```typescript
 * // Load from online store
 * const result = await loadBundleData({
 *   source: 'online_store',
 *   external_product_id: '123456789',
 *   country_code: 'US'
 * });
 *
 * if (result.success) {
 *   console.log('Bundle data:', result.data);
 * } else {
 *   console.error('Error:', result.error.message);
 * }
 *
 * // Load from Shopify Storefront API
 * const result2 = await loadBundleData({
 *   source: 'shopify_storefront',
 *   external_product_id: '123456789',
 *   country_code: 'US',
 *   shopify_storefront_access_token: 'your-token-here'
 * });
 * ```
 */
export async function loadBundleData(id: string, options?: Options): Promise<PublicBundleData> {
  if (options?.source === 'shopify_storefront') {
    return loadFromShopifyStorefrontApi(id, options);
  }

  return loadFromOnlineStore(id, options?.country_code);
}

/**
 * Load bundle data from the online store endpoint
 */
async function loadFromOnlineStore(id: string, country_code?: string): Promise<PublicBundleData> {
  if (!country_code) {
    const data = await shopifyAppProxyRequest<StorefrontBundleData>('get', `/bundle-data/${id}`);
    return mapOnlineStoreToPublicBundleData(data);
  }

  const payload = new FormData();
  payload.append('form_type', 'localization');
  payload.append('_method', 'put');
  payload.append('return_to', `${SHOPIFY_APP_PROXY_URL}/bundle-data/${id}`);
  payload.append('country_code', country_code);

  const headers = {
    'X-Recharge-Sdk-Fn': 'loadFromOnlineStore',
    'X-Recharge-Sdk-Version': '__SDK_VERSION__',
  };
  try {
    const data = await request<StorefrontBundleData>('post', '/localization', {
      headers: { 'Content-Type': 'multipart/form-data', ...headers },
      data: payload,
    });
    return mapOnlineStoreToPublicBundleData(data);
  } catch (error) {
    const data = await shopifyAppProxyRequest<StorefrontBundleData>('get', `/bundle-data/${id}`, {
      headers,
    });
    return mapOnlineStoreToPublicBundleData(data);
  }
}

/**
 * Load bundle data from Shopify Storefront API
 */
async function loadFromShopifyStorefrontApi(id: string, options: ShopifyStorefrontOptions): Promise<PublicBundleData> {
  const opts = getOptions();

  if (!opts.storeIdentifier) {
    throw new Error('Store identifier is required for Shopify Storefront API requests');
  }

  // Request data from Shopify Storefront API
  return {} as PublicBundleData;
}
